package com.htmlagent.shared

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Body of an error response, shared by backend and frontend.
 *
 * [code] is what the UI decides on; [detail] is text already written to be shown, never an
 * exception message, which could leak SQL or paths. The frontend uses [detail] as the fallback
 * for a code it does not know yet.
 */
@Serializable
data class ApiProblem(
    val code: ApiErrorCode,
    val detail: String,
)

/**
 * The errors the backend can answer with.
 *
 * An enum and not a set of string constants so the `when` that turns one into a message is
 * exhaustive: adding a case here stops the frontend compiling instead of leaving it to fall
 * through to a generic text. The wire format does not change — every entry keeps its
 * snake_case name through [SerialName].
 */
@Serializable
enum class ApiErrorCode {
    @SerialName("malformed_session_id")
    MALFORMED_SESSION_ID,

    @SerialName("invalid_user_id")
    INVALID_USER_ID,

    @SerialName("session_not_found")
    SESSION_NOT_FOUND,

    @SerialName("session_busy")
    SESSION_BUSY,

    @SerialName("session_protected")
    SESSION_PROTECTED,

    /** A guest used every turn they get; signing in with an email lifts the cap. */
    @SerialName("guest_limit_reached")
    GUEST_LIMIT_REACHED,

    @SerialName("empty_prompt")
    EMPTY_PROMPT,

    @SerialName("message_not_found")
    MESSAGE_NOT_FOUND,

    @SerialName("version_not_found")
    VERSION_NOT_FOUND,

    @SerialName("malformed_body")
    MALFORMED_BODY,

    @SerialName("malformed_request")
    MALFORMED_REQUEST,

    @SerialName("storage_failure")
    STORAGE_FAILURE,

    /** A reference photo was refused: too many, too heavy, or not a JPEG, PNG or WebP. */
    @SerialName("invalid_image")
    INVALID_IMAGE,

    @SerialName("invalid_email")
    INVALID_EMAIL,

    /** Too many access links asked for. The response carries `Retry-After`. */
    @SerialName("too_many_requests")
    TOO_MANY_REQUESTS,

    /** Too many new guests from the same address. The response carries `Retry-After`. */
    @SerialName("too_many_guests")
    TOO_MANY_GUESTS,

    /** The access email cannot be sent now; going on as a guest still works. */
    @SerialName("mail_unavailable")
    MAIL_UNAVAILABLE,

    /** The access link is unknown, used or expired. */
    @SerialName("invalid_link")
    INVALID_LINK,

    /** The number picked to approve a link is not the waiting browser's; the link is spent. */
    @SerialName("wrong_match")
    WRONG_MATCH,

    @SerialName("not_a_guest")
    NOT_A_GUEST,

    /** No session, or it expired: the page has to sign in again. */
    @SerialName("unauthenticated")
    UNAUTHENTICATED,
}
